import * as React from 'react';
import { Link } from 'react-router';
import styled, { keyframes } from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import { FlexColCenter, FlexBetween } from '../../Defaults/Flexbox/Flexbox';
import { ProductPriceCart } from './ProductPriceCart';
import { TagType, Tag } from './Tag';
import { FreeDelivery } from '../../Zemplin/Product/FreeDelivery';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { StockInfo } from './StockInfo';
import { FavoriteCompare } from './FavoriteCompare';

export interface Props {
  apiUrl: string;
  addToRequest: any;
  addToCart: any;
  product: any;
}

function ProductTabDefault({
  apiUrl,
  addToCart,
  addToRequest,
  product,
}: Props) {
  const {
    availid,
    availability,
    picture,
    url,
    name,
    price,
    oldPrice,
  } = getProductDefaultInfo(product, apiUrl);
  const isTag = product.is_new === 1 || product.is_sale === 1;
  return (
    <Wrapper>
      <TopBorder />
      <RightBorder />
      <TagsWrapper>
        {isTag &&
          (product.is_new ? (
            <Tag type={TagType.NEW} text={'NOVÉ'} />
          ) : (
            <Tag type={TagType.SALE} text={'%'} />
          ))}
        {/* currently turned off, turn off when API is available*/}
        {false && <FreeDelivery />}
      </TagsWrapper>
      <FavoriteCompareWrapper>
        <FavoriteCompare isFavorite={product.is_favorite} />
      </FavoriteCompareWrapper>
      <Link to={url}>
        <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
      </Link>
      <NameWrapper to={url}>
        <Name>{name}</Name>
        <Description>{name}</Description>
      </NameWrapper>
      <StyledProductPriceCart
        price={price}
        oldPrice={oldPrice}
        salePercentage={product.main_good.sale_percentage}
        isSale={product.is_sale}
        avail_id={availid}
        addToRequest={addToRequest}
        addToCart={addToCart}
      />
      <StockInfo avail_id={availid} availability={availability} />
    </Wrapper>
  );
}

export const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexColCenter)`
  animation: ${animateIn} 0.3s ease;
  width: 100%;
  max-width: 23.5%;
  margin: 0 ${rem(16)} ${rem(16)} 0;
  padding: ${rem(16)};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 1px 4px #00000014;
  border-radius: 4px;
  position: relative;
  justify-content: space-between;

  &:nth-child(4n) {
    margin-right: 0;

    ${({ theme }) => theme.mediab.l1300`
      margin-right: ${rem(16)};
    `}

    ${({ theme }) => theme.mediab.m810`
      margin-right: 0;
      padding-right:  ${rem(8)};
    `}
  }

  &:first-child,
  &:nth-child(2),
  &:first-child > :first-child,
  &:nth-child(2) > :first-child,
  &:nth-child(3) > :first-child,
  &:nth-child(3n) > :nth-child(2) {
    border: 0;
  }

  ${({ theme }) => theme.mediab.l1300`
    max-width: 47%;

    &:nth-child(2n) {
      margin-right: 0;
    }

    &:nth-of-type(3) > :first-child {
      border-top: ${theme.borders.primary};
    }

    &:nth-child(3n) > :nth-child(2) {
      border-right: ${theme.borders.primary};
    }

    &:first-child > :first-child,
    &:nth-child(2) > :first-child,
    &:nth-child(2n) > :nth-child(2) {
      border: 0;
    }
  `}

  ${({ theme }) => theme.mediab.s450`
    width: 100%;
    max-width: initial;
    min-width: initial;
    margin-right: 0;
    padding-bottom: ${rem(32)};

    & > :nth-child(2)  {
      border: 0;
    }

    &:nth-child(2n) {
      margin-right: 0;
    }

    &:first-child > :first-child,
    &:nth-child(2) > :first-child,
    &:nth-child(2n) > :nth-child(2) {
      border-right: 0;
      border-top: ${theme.borders.primary};
    }

    &:first-child > :nth-child(2), &:nth-child(3n) > :nth-child(2) {
      border-right: 0;
    }

    &:last-child {
      border-bottom: ${theme.borders.primary};
    }
  `}
`;

const StyledProductPriceCart = styled(ProductPriceCart)`
  margin-bottom: ${rem(16)};
`;

const Image = styled.img`
  margin: ${rem(32)} 0;
  position: relative;
  width: ${rem(160)};
  height: ${rem(160)};
  transition: transform 0.2s;

  &:hover {
    transform: scale(0.9);
  }
`;

const TopBorder = styled.div`
  border-top: ${({ theme }) => theme.borders.primary};
  position: absolute;
  left: 2%;
  right: 6%;
  top: 0;

  ${({ theme }) => theme.mediab.m810`
      left: 1%;
      right: 1%;
  `};
`;

const RightBorder = styled.div`
  border-right: ${({ theme }) => theme.borders.primary};
  position: absolute;
  bottom: 8%;
  top: 8%;
  right: 0;
`;

const NameWrapper = styled(Link)`
  text-decoration: none;
  width: 100%;
  margin-bottom: ${rem(24)};
`;

const Name = styled(TextP)`
  height: ${rem(38)};
  font-size: ${rem(16)};
  line-height: ${rem(19)};
  margin-bottom: ${rem(8)};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  width: 100%;
  font-weight: 500;
  transition: color 0.2s;

  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.m810`
      margin-bottom: ${rem(8)};
  `}
`;

const Description = styled(TextP)`
  height: ${rem(38)};
  text-align: center;
  color: ${({ theme }) => theme.colors.inactiveFontColor};
  font-size: ${rem(14)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const TagsWrapper = styled(FlexBetween)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(16)};

  ${({ theme }) => theme.mediab.m810`
      display: none;
  `}
`;

const FavoriteCompareWrapper = styled.div`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
  z-index: 100;
`;

const SecondaryText = styled(TextP)`
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};
  line-height: ${rem(20)};
  color: ${({ theme }) => theme.colors.placeholder};
`;

export { ProductTabDefault as DsiProductTabDefault };
